import { useState, useRef, useEffect, MouseEvent } from "react";
import { Button } from "@altimateai/lego";

interface ExpandableTextProps {
  children: React.ReactNode;
  lines?: number;
  moreText?: string;
  lessText?: string;
  buttonClassName?: string;
  onToggle?: (e: MouseEvent, truncated: boolean) => void;
  expandByDefault?: boolean;
}

const ExpandableText = ({
  children,
  lines = 3,
  moreText = "See more",
  lessText = "See less",
  buttonClassName = "",
  onToggle,
  expandByDefault = false,
}: ExpandableTextProps) => {
  const [isTruncated, setIsTruncated] = useState(!expandByDefault);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (contentRef.current) {
        setShowButton(contentRef.current.scrollHeight > contentRef.current.clientHeight);
      }
    };

    checkTruncation();

    // check only width changes
    let previousWidth = 0;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const currentWidth = entry.contentRect.width;
        if (currentWidth !== previousWidth) {
          previousWidth = currentWidth;
          checkTruncation();
        }
      }
    });

    if (contentRef.current) {
      previousWidth = contentRef.current.getBoundingClientRect().width;
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children, lines]);

  const handleToggle = (e: MouseEvent) => {
    const newTruncated = !isTruncated;
    setIsTruncated(newTruncated);
    e.stopPropagation();
    onToggle?.(e, newTruncated);
  };

  const truncateStyles: React.CSSProperties = isTruncated
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : {};

  return (
    <div>
      <div ref={contentRef} style={truncateStyles}>
        {children}
      </div>

      {showButton && (
        <>
          {!isTruncated && " "}
          <Button
            variant="link"
            size="sm"
            onClick={handleToggle}
            className={`al-inline al-p-0 al-h-auto al-text-primary al-text-xs ${buttonClassName}`}
          >
            {isTruncated ? moreText : lessText}
          </Button>
        </>
      )}
    </div>
  );
};

export default ExpandableText;
